import { Config } from '@shared/configs/config';
import * as moment from 'moment';
import { Languages, LanguageService } from '@core/services/language.service';
import localePL from '@fullcalendar/core/locales/pl';
import localeDE from '@fullcalendar/core/locales/de';
import localeEN from '@fullcalendar/core/locales/en-gb';
import { WindowHelper } from '@shared/helpers/window.helper';
import { ProjectsTimelineStorageService } from '../../shared/services/projects-timeline-storage.service';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { CalendarOptions } from '@fullcalendar/core';

export const projectsTimelineCalendarConfig = (
  languageService: LanguageService,
  getResourceLabelComponent,
  eventChange,
  eventClick,
  eventDidMount,
  projectsTimelineStorage: ProjectsTimelineStorageService,
  slotLabelClick,
  getProjectsFilterComponent
): CalendarOptions => {
  const getLocale = () => {
    switch (languageService.getCurrentLanguage()) {
      case Languages.PL:
        return localePL;
      case Languages.DE:
        return localeDE;
      default:
        return localeEN;
    }
  };

  return {
    plugins: [dayGridPlugin, interactionPlugin, resourceTimelinePlugin],
    schedulerLicenseKey: Config.FULL_CALENDAR_LICENSE_KEY,
    initialView: 'customThreeYearTimeline',
    height: '100%',
    locale: getLocale(),
    headerToolbar: false,
    resourceOrder: 'index',
    resourceAreaWidth: WindowHelper.isMoreMobileWidth ? '241px' : '140px',
    resourceAreaHeaderContent: getProjectsFilterComponent.bind(this),
    resourceLabelContent: getResourceLabelComponent.bind(this),
    resourcesInitiallyExpanded: true,
    eventChange: eventChange.bind(this),
    eventClick: eventClick.bind(this),
    eventDrop: eventDidMount.bind(this),
    eventDidMount: eventDidMount.bind(this),
    eventResize: eventDidMount.bind(this),
    lazyFetching: true,
    nowIndicator: true,
    eventOverlap: true,
    slotLabelDidMount: (e) => {
      e.el.addEventListener('click', () => {
        slotLabelClick(e);
      });

      if (
        (e.view.type === 'customThreeYearTimeline' && e.level === 1) ||
        e.view.type === 'resourceTimelineWeeksSlot' ||
        (e.view.type === 'resourceTimelineMonth' && e.level === 1)
      ) {
        e.el.classList.add('cursor-pointer');
      }
    },
    views: {
      resourceTimelineWeek: {
        type: 'resourceTimelineWeek',
        slotLabelFormat: [{ month: 'long' }, { weekday: 'short', day: 'numeric', month: 'numeric' }],
        slotLabelClassNames: 'no-weekend-marked',
        slotLaneClassNames: 'no-weekend-marked',
        slotDuration: '24:00:00',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit' },
        displayEventTime: false
      },
      resourceTimelineThreeWeeks: {
        type: 'resourceTimeline',
        slotLabelFormat: [{ month: 'long' }, { weekday: 'short', day: 'numeric', month: 'numeric' }],
        slotLabelClassNames: 'no-weekend-marked',
        slotLaneClassNames: 'no-weekend-marked',
        slotDuration: '24:00:00',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit' },
        displayEventTime: false,
        visibleRange: (currentDate) => {
          const start = moment(currentDate).subtract(1, 'week').startOf('week');
          const end = moment(currentDate).add(1, 'week').endOf('week');
          return { start: start.toDate(), end: end.toDate() };
        }
      },
      resourceTimelineThreeMonths: {
        type: 'resourceTimeline',
        slotLabelFormat: [{ month: 'long' }, { day: '2-digit' }],
        slotLabelClassNames: 'no-weekend-marked',
        slotLaneClassNames: 'no-weekend-marked',
        titleFormat: {
          week: 'short',
          year: 'numeric',
          month: 'long'
        },
        slotDuration: { days: 1 },
        visibleRange: function (currentDate) {
          const start = moment(currentDate).subtract(1, 'month').startOf('month');
          const end = moment(currentDate).add(1, 'month').endOf('month');
          return { start: start.toDate(), end: end.toDate() };
        },
        displayEventTime: false
      },
      resourceTimelineWeeksSlot: {
        type: 'resourceTimeline',
        slotLabelFormat: [{ month: 'long', year: 'numeric' }, { week: 'numeric' }],
        slotDuration: { weeks: 1 },
        slotLabelClassNames: 'no-weekend-marked',
        slotLaneClassNames: 'no-weekend-marked',
        titleFormat: {
          week: 'short',
          year: 'numeric',
          month: 'long'
        },
        visibleRange: function (currentDate) {
          const startYear = moment(currentDate).subtract(1, 'year').startOf('year');
          const endYear = moment(startYear).add(2, 'years').endOf('year');
          return { start: startYear.toDate(), end: endYear.toDate() };
        },
        displayEventTime: false
      },
      customThreeYearTimeline: {
        type: 'resourceTimeline',
        slotDuration: { days: 2 },
        slotMinWidth: 8,
        slotLabelFormat: [{ month: 'short', year: 'numeric' }, { day: 'numeric' }],
        visibleRange: function (currentDate) {
          const startYear = moment(currentDate).subtract(1, 'year').startOf('year');
          const endYear = moment(startYear).add(2, 'years').endOf('year');
          return { start: startYear.toDate(), end: endYear.toDate() };
        },
        titleFormat: { year: 'numeric', month: 'long' },
        displayEventTime: false,
        slotLabelClassNames: 'no-weekend-marked',
        slotLaneClassNames: 'no-weekend-marked',
        viewClassNames: 'year-without-days'
      }
    }
  };
};

export const PROJECT_STAGES_FILTER = [
  ProjectStage.STAGE_ESTIMATE,
  ProjectStage.STAGE_NEGOTIATIONS,
  ProjectStage.STAGE_WON,
  ProjectStage.STAGE_BEFORE_REALIZATION,
  ProjectStage.STAGE_REALIZATION
];
